import * as Billing from "@/models/billing";
import * as React from "react";

import {
  buildCompanyBillingPlanChangeFeedback,
  canCreateCompanyBillingCheckout,
  isCompanyBillingPaidStatus,
  listCompanyBillingSellableTargets,
  parseCompanyBillingSearch,
  selectCompanyBillingTarget,
  CompanyBillingPlanSelectionPage as TurboCompanyBillingPlanSelectionPage,
  showErrorToast,
} from "turboui";

import { useTranslation } from "react-i18next";
import { useLoadedData } from "../CompanyBillingPage/loader";
import { useLocation, useNavigate, useRouteLoaderData } from "react-router";
import { translationText } from "@/i18n";
import { usePaths } from "@/routes/paths";

interface CompanyRootData {
  company?: {
    name?: string | null;
  } | null;
}

export function Page() {
  const { t } = useTranslation();
  const billingActions = Billing.useBillingActions();
  const location = useLocation();
  const navigate = useNavigate();
  const paths = usePaths();
  const { billing: loadedBilling } = useLoadedData();
  const companyRootData = useRouteLoaderData("companyRoot") as CompanyRootData | undefined;

  const [billing, setBilling] = React.useState(loadedBilling);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  React.useEffect(() => {
    setBilling(loadedBilling);
  }, [loadedBilling]);

  const search = React.useMemo(() => parseCompanyBillingSearch(location.search), [location.search]);
  const selection = React.useMemo(() => selectCompanyBillingTarget(billing, search), [billing, search, t]);
  const canUseCheckout = canCreateCompanyBillingCheckout(billing.account.status);
  const canManagePaidSubscription = isCompanyBillingPaidStatus(billing.account.status);
  const companyName = companyRootData?.company?.name || translationText(t("Billing"));

  const navigateToSelection = React.useCallback(
    (target: TurboCompanyBillingPlanSelectionPage.BillingTarget | null, replace = false) => {
      if (!target) return;

      navigate(
        paths.companyBillingPlansPath({
          plan: target.plan,
          billingPeriod: target.billingInterval,
        }),
        { replace },
      );
    },
    [navigate, paths],
  );

  const handleSelectPlan = React.useCallback(
    (plan: TurboCompanyBillingPlanSelectionPage.Plan) => {
      const billingInterval = selection.target?.billingInterval || search.billingInterval || "monthly";
      navigateToSelection({ plan, billingInterval, product: null }, true);
    },
    [navigateToSelection, search.billingInterval, selection.target],
  );

  const handleSelectInterval = React.useCallback(
    (billingInterval: TurboCompanyBillingPlanSelectionPage.Interval) => {
      const plan = selection.target?.plan || search.plan || listCompanyBillingSellableTargets(billing)[0]?.plan;
      if (!plan) return;

      navigateToSelection({ plan, billingInterval, product: null }, true);
    },
    [billing, navigateToSelection, search.plan, selection.target],
  );

  const startCheckout = React.useCallback(async () => {
    setActionError(null);
    setIsSubmitting(true);

    const result = await billingActions.beginCheckout(selection.target);

    if (result.outcome === "missing_target") {
      setIsSubmitting(false);
      return;
    }

    if (result.outcome === "target_unavailable") {
      setIsSubmitting(false);
      setActionError(t("That plan is no longer available. Choose another plan."));
      showErrorToast(t("Checkout unavailable"), t("That plan is no longer available. Choose another plan."));
      return;
    }

    if (result.outcome === "session_created") {
      Billing.redirectToExternalBillingUrl(result.session.url);
      return;
    }

    if (result.billing) {
      setBilling(result.billing);
    }

    setActionError(t("We couldn't start checkout right now. Please try again."));
    showErrorToast(t("Failed to start checkout"), t("We couldn't start checkout right now. Please try again."));
    setIsSubmitting(false);
  }, [billingActions, selection.target, t]);

  const submitPlanChange = React.useCallback(async () => {
    setActionError(null);
    setIsSubmitting(true);

    const result = await billingActions.changePlan(selection.target);

    if (result.outcome === "missing_target") {
      setIsSubmitting(false);
      return;
    }

    if (result.outcome === "target_unavailable") {
      setIsSubmitting(false);
      setActionError(t("That plan is no longer available. Choose another plan."));
      showErrorToast(t("Plan unavailable"), t("That plan is no longer available. Choose another plan."));
      return;
    }

    if (result.outcome === "billing_updated") {
      navigate(paths.companyBillingPath(), {
        state: {
          billing: result.billing,
          feedback: buildCompanyBillingPlanChangeFeedback(result.billing),
        },
      });
      return;
    }

    if (result.billing) {
      setBilling(result.billing);
    }

    setActionError(t("We couldn't change the plan right now. Please try again."));
    showErrorToast(t("Failed to change plan"), t("We couldn't change the plan right now. Please try again."));
    setIsSubmitting(false);
  }, [billingActions, navigate, paths, selection.target, t]);

  const handleSubmit = React.useCallback(() => {
    if (canManagePaidSubscription) {
      void submitPlanChange();
      return;
    }

    if (canUseCheckout) {
      void startCheckout();
    }
  }, [canManagePaidSubscription, canUseCheckout, startCheckout, submitPlanChange]);

  return (
    <TurboCompanyBillingPlanSelectionPage
      title={[companyName, translationText(t("Choose a plan"))]}
      navigation={[
        { label: t("Company Administration"), to: paths.companyAdminPath() },
        { label: t("Billing"), to: paths.companyBillingPath() },
      ]}
      billing={billing}
      selection={selection}
      actionError={actionError}
      isSubmitting={isSubmitting}
      onSelectPlan={handleSelectPlan}
      onSelectInterval={handleSelectInterval}
      onSubmit={handleSubmit}
      testId="company-billing-plan-selection-page"
    />
  );
}
